import React, { useState, useEffect, useRef } from 'react';
import { kbWebScraper } from '../api';
import LoadingSpinner from './LoadingSpinner';
import AnimatedDropdown from './AnimatedDropdown';
import {
  X, Bot, FileText, Sparkles, AlertTriangle, CheckCircle, XCircle, ArrowRight, Info, Files, ShieldCheck
} from 'lucide-react';

// Reviews a saved website's newest files against what it last put into an agent's
// knowledge base, then replaces those documents once the user confirms.

const FILE_STATUS = {
  changed: { label: 'Changed', className: 'bg-warning/20 text-foreground' },
  added: { label: 'New', className: 'bg-success/15 text-success' },
  removed: { label: 'Removed', className: 'bg-destructive/15 text-destructive' },
  unchanged: { label: 'Same', className: 'bg-muted/50 text-muted-foreground' },
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : '');

const errorText = (e) => {
  const detail = e?.response?.data?.detail;
  return typeof detail === 'string' ? detail : (e?.message || 'Something went wrong');
};

const secondaryBtn = 'px-4 py-2 rounded-lg text-sm font-medium border border-border/60 text-foreground hover:border-primary/40 hover:bg-muted/40 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed';
const primaryBtn = 'px-5 py-2 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 text-sm font-medium btn-hover-gradient';

const Segments = ({ segs, tone }) => segs.map(([text, changed], i) => (
  <span key={i} className={changed ? (tone === 'old' ? 'bg-destructive/35 rounded-sm' : 'bg-success/35 rounded-sm') : undefined}>{text}</span>
));

const Cell = ({ no, children, tone }) => {
  const bg = tone === 'old' ? 'bg-destructive/10' : tone === 'new' ? 'bg-success/10' : '';
  return (
    <>
      <td className={`w-10 pr-2 text-right align-top select-none text-muted-foreground/70 ${bg}`}>{no ?? ''}</td>
      <td className={`align-top whitespace-pre-wrap break-words pr-3 ${bg}`}>{children}</td>
    </>
  );
};

const DiffTable = ({ file }) => (
  <table className="w-full table-fixed text-xs font-mono leading-relaxed border-collapse">
    <colgroup><col className="w-10" /><col /><col className="w-10" /><col /></colgroup>
    <thead>
      <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
        <th colSpan={2} className="text-left font-semibold pb-2 pl-12">In the agent now{file.old_filename ? `: ${file.old_filename}` : ''}</th>
        <th colSpan={2} className="text-left font-semibold pb-2 pl-12 border-l border-border/60">After update{file.new_filename ? `: ${file.new_filename}` : ''}</th>
      </tr>
    </thead>
    <tbody>
      {file.rows.map((r, i) => {
        if (r.t === 'skip') {
          return (
            <tr key={i}>
              <td colSpan={4} className="text-center text-muted-foreground bg-muted/30 py-1 my-1">
                {r.n} unchanged line{r.n === 1 ? '' : 's'}
              </td>
            </tr>
          );
        }
        return (
          <tr key={i}>
            {r.t === 'add' ? <Cell /> : (
              <Cell no={r.l} tone={r.t === 'eq' ? null : 'old'}>
                {r.t === 'chg' ? <Segments segs={r.as} tone="old" /> : r.a}
              </Cell>
            )}
            {r.t === 'del' ? <><td className="border-l border-border/60" /><td /></> : (
              <>
                <td className={`w-10 pr-2 text-right align-top select-none text-muted-foreground/70 border-l border-border/60 ${r.t === 'eq' ? '' : 'bg-success/10'}`}>{r.r}</td>
                <td className={`align-top whitespace-pre-wrap break-words pr-3 ${r.t === 'eq' ? '' : 'bg-success/10'}`}>
                  {r.t === 'chg' ? <Segments segs={r.bs} tone="new" /> : r.b}
                </td>
              </>
            )}
          </tr>
        );
      })}
    </tbody>
  </table>
);

const KbAgentReviewModal = ({ site, agentList, initialVariant = 'formatted', onClose, onDone }) => {
  const [agentId, setAgentId] = useState(site.agent_id || '');
  const [variant, setVariant] = useState(initialVariant);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [needsAgent, setNeedsAgent] = useState(false);
  const [fileIdx, setFileIdx] = useState(0);
  const [extraIds, setExtraIds] = useState(() => new Set());
  const [confirming, setConfirming] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearTimeout(pollRef.current), []);

  // A replace already running for this site (e.g. started before a page reload)
  useEffect(() => {
    kbWebScraper.getAgentKbReplace(site.id)
      .then((r) => { if (r.data?.status === 'running') { setJob(r.data); poll(); } })
      .catch(() => {});
  }, [site.id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setConfirming(false);
    kbWebScraper.reviewAgentKb(site.id, { variant, ...(agentId ? { agent_id: agentId } : {}) })
      .then((r) => {
        if (cancelled) return;
        setReview(r.data);
        setNeedsAgent(false);
        if (!agentId) setAgentId(r.data.agent_id);
        setFileIdx(0);
        setExtraIds(new Set(r.data.other_docs.map((d) => d.id)));
      })
      .catch((e) => {
        if (cancelled) return;
        setReview(null);
        if (!agentId && e?.response?.status === 400) setNeedsAgent(true);
        else setError(errorText(e));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [site.id, agentId, variant]);

  const poll = () => {
    clearTimeout(pollRef.current);
    pollRef.current = setTimeout(async () => {
      try {
        const r = await kbWebScraper.getAgentKbReplace(site.id);
        setJob(r.data);
        if (r.data.status === 'running') poll();
        else if (r.data.status === 'completed') onDone?.(r.data, review?.agent_name);
      } catch {
        poll();
      }
    }, 1500);
  };

  const replace = async () => {
    setError('');
    try {
      const r = await kbWebScraper.replaceAgentKb(site.id, {
        agent_id: review.agent_id,
        variant,
        fingerprint: review.fingerprint,
        also_replace_doc_ids: [...extraIds],
      });
      setConfirming(false);
      setJob(r.data);
      poll();
    } catch (e) {
      setConfirming(false);
      setError(errorText(e));
    }
  };

  const toggleExtra = (id) => setExtraIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const diff = review?.diff;
  const totals = diff?.totals;
  const file = diff?.files?.[fileIdx];
  const removeCount = (review?.current.files.length || 0) + extraIds.size;
  const addCount = review?.new.files.length || 0;
  const nothingToDo = diff?.identical && extraIds.size === 0;
  const variantMismatch = review?.current.variant && review.current.variant !== variant;
  const running = job?.status === 'running';
  const finished = job && job.status !== 'running';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border/60 rounded-xl shadow-elegant w-full max-w-7xl flex flex-col" style={{ height: 'calc(100vh - 48px)' }}>
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-5 border-b border-border/60 flex-wrap">
          <div className="min-w-0">
            <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Bot className="w-5 h-5 text-primary" /> Update agent knowledge base
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {site.name} <ArrowRight className="inline w-3.5 h-3.5 mx-1" /> {review?.agent_name || 'pick an agent'}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-56">
              <AnimatedDropdown
                value={agentId}
                onChange={(value) => setAgentId(value)}
                options={[{ value: '', label: 'Pick an agent' }, ...agentList.map((a) => ({ value: a.id, label: a.name }))]}
                placeholder="Pick an agent"
                icon={Bot}
              />
            </div>
            <div className="inline-flex rounded-lg border border-border/60 p-1 bg-muted/20">
              {[{ id: 'formatted', label: 'Formatted', icon: FileText }, { id: 'ai', label: 'AI-optimized', icon: Sparkles }].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setVariant(t.id)}
                  disabled={running || (t.id === 'ai' && review && !review.has_ai)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed ${variant === t.id ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted/40'}`}
                >
                  <t.icon className="w-4 h-4" /> {t.label}
                </button>
              ))}
            </div>
            <button onClick={onClose} className="p-2 rounded-lg text-muted-foreground hover:text-foreground" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 flex flex-col p-5 gap-4">
          {error && (
            <div className="flex items-start gap-3 p-3 rounded-lg border border-destructive/30 bg-destructive/10 text-destructive text-sm">
              <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> <div className="flex-1">{error}</div>
            </div>
          )}

          {needsAgent && !loading && (
            <div className="flex items-start gap-3 p-4 rounded-lg border border-border/60 bg-muted/20 text-sm text-foreground">
              <Info className="w-4 h-4 mt-0.5 text-primary" /> Pick the agent whose knowledge base this website should update.
            </div>
          )}

          {loading && <LoadingSpinner size="sm" text="Comparing the agent's files with the newest scrape..." />}

          {!loading && review && !job && (
            <>
              {/* Summary */}
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {review.current.files.length ? (
                  <span className="text-muted-foreground">
                    The agent has {review.current.files.length} file{review.current.files.length === 1 ? '' : 's'} from this website
                    {review.current.pushed_at && `, added ${formatDate(review.current.pushed_at)}`}.
                  </span>
                ) : (
                  <span className="text-muted-foreground">The agent has no files from this website yet, so everything below is new.</span>
                )}
                {diff.identical ? (
                  <span className="px-2 py-0.5 rounded-full bg-success/15 text-success text-xs font-medium">No changes</span>
                ) : (
                  <>
                    {totals.changed > 0 && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${FILE_STATUS.changed.className}`}>{totals.changed} changed</span>}
                    {totals.added > 0 && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${FILE_STATUS.added.className}`}>{totals.added} new</span>}
                    {totals.removed > 0 && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${FILE_STATUS.removed.className}`}>{totals.removed} removed</span>}
                    {totals.unchanged > 0 && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${FILE_STATUS.unchanged.className}`}>{totals.unchanged} same</span>}
                    <span className="text-xs text-success font-mono">+{totals.lines_added}</span>
                    <span className="text-xs text-destructive font-mono">-{totals.lines_removed}</span>
                  </>
                )}
              </div>

              {(variantMismatch || (variant === 'ai' && review.ai_stale) || review.previous_agent || review.current.missing > 0) && (
                <div className="space-y-2">
                  {variantMismatch && (
                    <Notice>The agent has the {review.current.variant === 'ai' ? 'AI-optimized' : 'formatted'} version, and you are comparing the {variant === 'ai' ? 'AI-optimized' : 'formatted'} one, so most lines show as changed.{review.current.variant === 'ai' && ' To keep AI files, close this, run Optimize with AI on the new scrape, then update the agent.'}</Notice>
                  )}
                  {variant === 'ai' && review.ai_stale && (
                    <Notice>These AI files were made before the newest sync, so they do not include its changes.</Notice>
                  )}
                  {review.previous_agent && (
                    <Notice>This website last updated {review.previous_agent.agent_name || 'another agent'}. Its {review.previous_agent.files} file{review.previous_agent.files === 1 ? '' : 's'} there stay as they are.</Notice>
                  )}
                  {review.current.missing > 0 && (
                    <Notice>{review.current.missing} file{review.current.missing === 1 ? ' this website added was' : 's this website added were'} deleted from the agent's knowledge base by hand, so {review.current.missing === 1 ? 'it is' : 'they are'} not shown here.</Notice>
                  )}
                </div>
              )}

              {/* Files + diff */}
              <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
                <div className="overflow-y-auto space-y-2 pr-1">
                  {diff.files.map((f, i) => (
                    <button
                      key={`${f.old_filename}-${f.new_filename}`}
                      onClick={() => setFileIdx(i)}
                      className={`w-full text-left rounded-lg border p-3 transition-colors ${i === fileIdx ? 'border-primary bg-primary/10' : 'border-border/60 hover:border-primary/40'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${FILE_STATUS[f.status].className}`}>{FILE_STATUS[f.status].label}</span>
                        {f.status !== 'unchanged' && (
                          <span className="text-[11px] font-mono"><span className="text-success">+{f.lines_added}</span> <span className="text-destructive">-{f.lines_removed}</span></span>
                        )}
                      </div>
                      <div className="text-xs text-foreground mt-1.5 break-all flex items-start gap-1.5">
                        <Files className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-primary" /> {f.new_filename || f.old_filename}
                      </div>
                      {f.old_filename && f.new_filename && f.old_filename !== f.new_filename && (
                        <div className="text-[11px] text-muted-foreground break-all mt-0.5">was {f.old_filename}</div>
                      )}
                    </button>
                  ))}
                </div>
                <div className="min-h-0 overflow-auto rounded-lg border border-border/60 bg-background/50 p-3">
                  {file && (file.rows.length ? <DiffTable file={file} /> : <p className="text-sm text-muted-foreground">Empty file.</p>)}
                </div>
              </div>

              {review.other_docs.length > 0 && (
                <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm">
                  <p className="font-medium text-foreground mb-1">The agent also has {review.other_docs.length} document{review.other_docs.length === 1 ? '' : 's'} with the same name, uploaded on the Knowledge Base page.</p>
                  <p className="text-xs text-muted-foreground mb-2">Their old content is not stored here, so it cannot be compared. Replace them to avoid the agent answering from two versions.</p>
                  <div className="space-y-1">
                    {review.other_docs.map((d) => (
                      <label key={d.id} className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" className="w-4 h-4 accent-[hsl(var(--primary))]" checked={extraIds.has(d.id)} onChange={() => toggleExtra(d.id)} />
                        <span className="text-foreground break-all">{d.filename}</span>
                        <span className="text-muted-foreground">uploaded {formatDate(d.created_at)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Replace progress / result */}
          {job && (
            <div className="max-w-2xl w-full mx-auto mt-6 space-y-4">
              {running && (
                <>
                  <div className="flex items-center gap-3"><LoadingSpinner size="sm" /><span className="text-foreground font-medium">{job.stage}</span></div>
                  <div className="w-full h-2 rounded-full bg-muted/50 overflow-hidden">
                    <div className="h-full bg-gradient-button transition-all duration-500" style={{ width: `${Math.max(3, job.progress || 0)}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground">New files go in first, then the old ones come out, so calls keep getting answers the whole time. You can close this window; it keeps going.</p>
                </>
              )}
              {job.status === 'completed' && (
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-success flex-shrink-0" />
                  <div>
                    <p className="text-foreground font-medium">Knowledge base updated</p>
                    <p className="text-sm text-muted-foreground mt-1">Added {job.uploaded} file{job.uploaded === 1 ? '' : 's'} and removed {job.deleted}. The agent answers from the new content on its next retrieval.</p>
                  </div>
                </div>
              )}
              {job.status === 'failed' && (
                <div className="flex items-start gap-3">
                  <XCircle className="w-6 h-6 text-destructive flex-shrink-0" />
                  <div>
                    <p className="text-foreground font-medium">Update failed</p>
                    <p className="text-sm text-muted-foreground mt-1">{job.error}</p>
                  </div>
                </div>
              )}
              {job.warnings?.length > 0 && (
                <ul className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-xs text-foreground space-y-1">
                  {job.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-border/60 flex items-center justify-between gap-3 flex-wrap">
          {finished ? (
            <>
              <span />
              <button onClick={onClose} className={primaryBtn}>Close</button>
            </>
          ) : confirming ? (
            <>
              <div className="flex items-start gap-2 text-sm text-foreground">
                <ShieldCheck className="w-4 h-4 text-warning mt-0.5" />
                <span>
                  Add {addCount} new file{addCount === 1 ? '' : 's'} to <b>{review.agent_name}</b>
                  {removeCount > 0 ? <>, then delete the {removeCount} file{removeCount === 1 ? '' : 's'} they replace. This cannot be undone.</> : '.'}
                </span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setConfirming(false)} className={secondaryBtn}>Back</button>
                <button onClick={replace} className={primaryBtn}>Yes, update the knowledge base</button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                {running ? 'Updating the knowledge base. This keeps going if you close this window.'
                  : nothingToDo ? 'The agent already has exactly these files.' : 'Nothing changes in the agent until you confirm.'}
              </p>
              <div className="flex gap-2">
                <button onClick={onClose} className={secondaryBtn}>{running ? 'Close' : 'Not now'}</button>
                {!running && (
                  <button onClick={() => setConfirming(true)} disabled={loading || !review || nothingToDo} className={primaryBtn}>
                    {review?.current.files.length ? 'Replace in agent KB' : 'Add to agent KB'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Notice = ({ children }) => (
  <div className="flex items-start gap-2 p-2.5 rounded-lg border border-warning/40 bg-warning/10 text-xs text-foreground">
    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-warning" /> <span>{children}</span>
  </div>
);

export default KbAgentReviewModal;
